import { useEffect, useState } from "react";
import supabase from "../../supabaseClient";
import { TASK_FEEDBACK_OPTIONS, getTaskFeedbackLabel } from "../../constants/taskFeedback";

export default function TaskFeedbackReportModal({ task, students, onClose }) {
  const [groupedStudents, setGroupedStudents] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadReport() {
      if (!task?.id) return;

      setIsLoading(true);
      const studentNameById = new Map(
        (students || []).map((student) => [student.id, student.student_name])
      );

      const { data, error } = await supabase
        .from("task_status")
        .select("student_id, feedback, completed")
        .eq("task_id", task.id)
        .eq("completed", true);

      if (error) {
        console.error("Error loading feedback report:", error);
        setGroupedStudents({});
        setIsLoading(false);
        return;
      }

      const groups = Object.fromEntries(
        TASK_FEEDBACK_OPTIONS.map((option) => [option, []])
      );
      groups.none = [];

      (data || []).forEach((row) => {
        const studentName = studentNameById.get(row.student_id);
        if (!studentName) return;

        if (TASK_FEEDBACK_OPTIONS.includes(row.feedback)) {
          groups[row.feedback].push(studentName);
        } else {
          groups.none.push(studentName);
        }
      });

      Object.keys(groups).forEach((key) => {
        groups[key].sort((a, b) => a.localeCompare(b, "nl"));
      });

      setGroupedStudents(groups);
      setIsLoading(false);
    }

    loadReport();
  }, [students, task]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold">Feedback report</h2>
            <p className="text-gray-600 mt-1">{task.title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        {isLoading ? (
          <p className="text-gray-500 italic">Feedback laden...</p>
        ) : (
          <div className="space-y-4">
            {TASK_FEEDBACK_OPTIONS.map((option) => (
              <section key={option} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h3 className="font-semibold">{getTaskFeedbackLabel(option)}</h3>
                  <span className="text-sm text-gray-500">
                    {groupedStudents[option]?.length || 0}
                  </span>
                </div>
                {groupedStudents[option]?.length ? (
                  <ul className="space-y-1 text-sm text-gray-700">
                    {groupedStudents[option].map((studentName) => (
                      <li key={`${option}-${studentName}`}>{studentName}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">Nog geen leerlingen.</p>
                )}
              </section>
            ))}

            {groupedStudents.none?.length > 0 && (
              <section className="border rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h3 className="font-semibold">Geen feedback gekozen</h3>
                  <span className="text-sm text-gray-500">
                    {groupedStudents.none.length}
                  </span>
                </div>
                <ul className="space-y-1 text-sm text-gray-700">
                  {groupedStudents.none.map((studentName) => (
                    <li key={`none-${studentName}`}>{studentName}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
